import { useMemo } from 'react';
import type { ComponentPropsWithoutRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { Components } from 'react-markdown';
import { Link } from 'react-router-dom';
import { helpTopics } from '@/constants/helpContent';

interface HelpContentProps {
  content: string;
  baseImagePath?: string;
  currentTopicId?: string; // 現在のトピックID（リンク解決に使用）
}

type MarkdownCodeProps = ComponentPropsWithoutRef<'code'> & { inline?: boolean };

/**
 * Markdownコンテンツ表示コンポーネント
 *
 * セキュリティ対策:
 * - XSS対策: skipHtml={true}でHTMLタグを無効化
 * - 画像のdata URI、javascript:スキームを拒否
 * - 外部リンクにrel="noopener noreferrer"を付与
 */
export function HelpContent({ content, baseImagePath = '/help', currentTopicId }: HelpContentProps) {
  // 現在のトピックのカテゴリを取得
  const currentTopic = currentTopicId ? helpTopics.find(t => t.id === currentTopicId) : undefined;
  const currentCategory = currentTopic ? currentTopic.category : null;

  // メモ化でパフォーマンス向上
  const components = useMemo<Components>(
    () => ({
      // 画像パスの自動解決（セキュリティチェック付き）
      img: ({ ...props }) => {
        const src = props.src || '';

        // データURIやjavascript:スキームを拒否
        if (src.startsWith('data:') || src.startsWith('javascript:')) {
          return null;
        }

        const resolvedSrc = src.startsWith('http')
          ? src
          : `${baseImagePath}/${src}`;

        return (
          <img
            {...props}
            src={resolvedSrc}
            alt={props.alt || '画像'}
            loading="lazy" // 遅延読み込み
            decoding="async" // 非同期デコード
            style={{ maxWidth: '100%', height: 'auto' }}
          />
        );
      },

      // リンクのセキュリティ強化と.mdリンクの自動変換
      a: ({ ...props }) => {
        const href = props.href || '';
        const isExternal = href.startsWith('http');

        // .mdリンクを適切なURLに変換
        if (href.endsWith('.md')) {
          // 相対パスから絶対パスに変換
          // 例: ../equipment/register.md → /help/equipment/register.md
          //     register.md → 現在のカテゴリ/register.md

          let normalizedPath = href;

          // ../を削除して正規化
          normalizedPath = normalizedPath.replace(/\.\.\//g, '');

          // .mdを削除
          normalizedPath = normalizedPath.replace(/\.md$/, '');

          // helpTopicsから一致するトピックを探す
          // 同じカテゴリ内のトピックを優先
          const topicsToSearch = currentCategory
            ? [
                ...helpTopics.filter(t => t.category === currentCategory),
                ...helpTopics.filter(t => t.category !== currentCategory),
              ]
            : helpTopics;

          const matchingTopic = topicsToSearch.find((topic) => {
            // markdownPathから推測
            // 例: /help/equipment/register.md
            const pathParts = topic.markdownPath.split('/');
            const category = pathParts[pathParts.length - 2]; // equipment
            const filename = pathParts[pathParts.length - 1].replace('.md', ''); // register

            // パターン1: "register.md" のような単純なファイル名
            if (normalizedPath === filename) {
              return true;
            }

            // パターン2: "equipment/register" のようなカテゴリ付き
            if (normalizedPath === `${category}/${filename}`) {
              return true;
            }

            return false;
          });

          if (matchingTopic) {
            // React Router Linkを使用
            return (
              <Link to={`/help/${matchingTopic.id}`} {...props}>
                {props.children}
              </Link>
            );
          }
        }

        // 外部リンクまたは通常のリンク
        return (
          <a
            {...props}
            rel={isExternal ? 'noopener noreferrer' : undefined}
            target={isExternal ? '_blank' : undefined}
          />
        );
      },

      // コードブロックのスタイリング
      code: ({ inline, ...props }: MarkdownCodeProps) =>
        inline ? (
          <code className="inline-code" {...props} />
        ) : (
          <code className="code-block" {...props} />
        ),

      // 見出しにアンカーリンク追加
      h2: ({ children, ...props }) => {
        const id = String(children).toLowerCase().replace(/\s+/g, '-');
        return (
          <h2 id={id} {...props}>
            {children}
          </h2>
        );
      },

      h3: ({ children, ...props }) => {
        const id = String(children).toLowerCase().replace(/\s+/g, '-');
        return (
          <h3 id={id} {...props}>
            {children}
          </h3>
        );
      },
    }),
    [baseImagePath, currentCategory]
  );

  return (
    <div className="help-content">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        skipHtml={true} // HTMLタグを許可しない（XSS対策）
        components={components}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}
